import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../Context/AuthContext";
import useSWR from "swr";
import {
  orderUrls,
  StaticPageApiUrls,
  UserApiUrls,
} from "../../Api/Auth/ApiUrls";
import {
  customFetcher,
  fetcher,
  sendPostRequestAuth,
} from "../../Api/swrConfig";
import useSWRMutation from "swr/mutation";
import { toast } from "react-toastify";
import { Loader } from "../../Components/Common/Loader";
import { motion } from "framer-motion";
import HeroSection from "../../Components/Common/HeroSection";
import { publicUrls, studentUrls } from "../../Api/Auth/AppUrls";
import Button from "../../Components/Common/Button";
import Modal from "../../Components/Common/Modal";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import Input from "../../Components/Common/Input";
import useToastError from "../../hooks/useErrorToast";

const stripePromise = loadStripe(process.env.REACT_APP_Publishable_Key);
const cardElementOptions = {
  style: {
    base: {
      fontSize: "16px",
      color: "#333",
      "::placeholder": { color: "#aab7c4" },
    },
    invalid: { color: "#fa755a" },
  },
};

const PaymentForm = ({ clientSecret, onClose, mutate, item }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [saveCard, setSaveCard] = useState(false);

  const { data } = useSWR(UserApiUrls.savedCards, fetcher);
  const savedCards = data?.cards || [];

  const { trigger } = useSWRMutation(orderUrls.payment, sendPostRequestAuth, {
    onSuccess: (data) => {
      mutate();
      toast.info(
        `You can access your quiz bank from your student dashboard too`
      );
    },
    onError: (error) => {
      toast.error(error.message || "Payment failed. Please try again.");
    },
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;
    setIsProcessing(true);
    try {
      let paymentMethodId;

      if (selectedCard) {
        paymentMethodId = selectedCard; // Use selected saved card
      } else {
        const cardElement = elements.getElement(CardNumberElement);
        if (!cardElement) {
          toast.error("Card details not found. Please try again.");
          setIsProcessing(false);
          return;
        }

        // Create a payment method using the card details
        const { paymentMethod, error: pmError } =
          await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
          });

        if (pmError) {
          toast.error(pmError.message || "Failed to create payment method.");
          setIsProcessing(false);
          return;
        }
        paymentMethodId = paymentMethod.id;
      }

      // Confirm payment
      const { paymentIntent, error: confirmError } =
        await stripe.confirmCardPayment(clientSecret, {
          payment_method: paymentMethodId,
          setup_future_usage: "off_session",
        });
      if (confirmError) {
        toast.error(
          confirmError.message || "Payment failed. Please try again."
        );
      } else if (paymentIntent?.status === "succeeded") {
        toast.success("Payment successful!");

        // Send request to backend to update order status
        await trigger({
          paymentIntentId: paymentIntent.id,
          itemName: item?.displayTitle || item?.title || "Quiz Bank(Civil)",
          saveCard: saveCard,
        });

        onClose();
      }
    } catch (err) {
      const dupCardError =
        err?.response?.data?.message ===
        "The payment method you provided is already attached to your saved cards list. Please use your saved card instead.";
      if (dupCardError) {
        return toast.error(err?.response?.data?.message);
      }
      console.error("Payment error:", err);
      toast.error("An unexpected error occurred.");
    }
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-4 rounded-lg p-3">
      {savedCards.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Saved Cards
          </label>
          <select
            className="p-2 border rounded-md w-full"
            value={selectedCard || ""}
            onChange={(e) => setSelectedCard(e.target.value || null)}
          >
            <option value="">Use a new card</option>
            {savedCards.map((card) => (
              <option key={card.id} value={card.id}>
                {`**** **** **** ${card.card.last4
                  } (${card.card.brand.toUpperCase()})`}
              </option>
            ))}
          </select>
        </div>
      )}

      {!selectedCard && (
        <>
          <label className="block text-sm font-medium text-gray-700">
            Card Number
          </label>
          <CardNumberElement
            className="p-2 border rounded-md"
            options={cardElementOptions}
          />

          <div className="flex space-x-4">
            <div className="w-1/2">
              <label className="block text-sm font-medium text-gray-700">
                Expiry Date
              </label>
              <CardExpiryElement
                className="p-2 border rounded-md w-full"
                options={cardElementOptions}
              />
            </div>

            <div className="w-1/2">
              <label className="block text-sm font-medium text-gray-700">
                CVC
              </label>
              <CardCvcElement
                className="p-2 border rounded-md w-full"
                options={cardElementOptions}
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="saveCard"
              checked={saveCard}
              onChange={() => setSaveCard(!saveCard)}
              className="h-4 w-4"
            />
            <label htmlFor="saveCard" className="text-sm text-gray-700">
              Save this card for future payments
            </label>
          </div>
        </>
      )}

      <Button
        type="primary"
        className="mt-4 w-full"
        disabled={isProcessing}
        isLoading={isProcessing}
      >
        {isProcessing ? "Processing..." : "Pay Now"}
      </Button>
    </form>
  );
};

const QuizBank = () => {
  const location = useLocation();
  const { showErrorToast } = useToastError();
  const { token, userData } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [paymentConfirmScreen, setPaymentConfirmScreen] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState("");
  const [discountedPrice, setDiscountedPrice] = useState(null);
  const slug = location.pathname.replace("/civil/", "");
  const slugWithQuery = slug + "?subscription=true";
  const navigate = useNavigate();
  const { data, isLoading, mutate } = useSWR(
    slugWithQuery ? StaticPageApiUrls.info(slugWithQuery) : null,
    (url) => customFetcher(url, token)
  );
  const content = data?.data?.content ? JSON.parse(data?.data?.content) : null;

  const { trigger: promoTrigger } = useSWRMutation(
    orderUrls.promo,
    sendPostRequestAuth,
    {
      onSuccess: (data) => {
        const calculatedPrice = content?.subscription_price - (content?.subscription_price * data?.discount) / 100;
        setDiscountedPrice(parseFloat(calculatedPrice.toFixed(2)));
        setAppliedPromo(promoCode);
        toast.success(`Promo Code applied successfully`);
      },
      onError: (error) => {
        console.log(error);
        showErrorToast(error || "Invalid promo code. Please try again.");
      },
    }
  );

  const onBuyClick = (data) => {
    setPaymentConfirmScreen(true);
    setIsModalOpen(true);
  };

  const onBuy = async (data) => {
    const payload = {
      userId: userData?._id,
      subscriptionId: data._id,
      promo: discountedPrice ? appliedPromo : undefined,
      slug: slug,
      customer: {
        id: userData?._id,
        name: userData?.name,
        email: userData?.email,
      },
    };
    await purchaseItem(payload);
  };

  const handleApplyPromo = async (e) => {
    e.preventDefault();
    if (!promoCode) {
      return toast.error("Enter Promo Code");
    }
    await promoTrigger({
      promoCode: promoCode,
      slug: slug,
      price: content?.subscription_price,
    });
  };

  const { trigger: purchaseItem, isMutating } = useSWRMutation(
    orderUrls.create,
    sendPostRequestAuth,
    {
      onSuccess: (data) => {
        toast.success("Processing payment...");
        setClientSecret(data.clientSecret);
        setPaymentConfirmScreen(false);
        // setIsModalOpen(true);
      },
      onError: (error) => {
        toast.error(error.message || "Payment failed. Please try again.");
      },
    }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 1, y: -40 }} // Start above the view
      whileInView={{ opacity: 1, y: 0 }} // End at normal position
      transition={{ duration: 1, ease: "easeOut" }} // Longer transition for more emphasis
    >
      <HeroSection
        title={data?.data?.displayTitle || data?.data?.title || "Quiz Bank"}
        backgroundImage="https://storage.googleapis.com/road-to-fe/uploads/1741417535659-07d447cd-82a2-4b1b-8c08-9fffeaaefa18-colleagues-doing-team-work-project_23-2149361607.avif"
        subtitle={data?.data?.headerDescription || "One Platform Many Courses"}
      />
      <div className="gap-3 container mx-auto sm:mt-12 mt-4 flex justify-center md:justify-between items-start flex-wrap md:flex-nowrap px-2">
        <div
          className="max-w-5xl w-full"
          dangerouslySetInnerHTML={{ __html: data?.data?.bodyDescription }}
        />
        <div>
          <img
            src={data?.data?.image}
            alt="Tutoring"
            className="w-full h-72 object-cover rounded-md"
          />
        </div>
      </div>
      <h2 className="text-3xl font-semibold text-primary text-center mt-4">
        {`Try our ${data?.data?.displayTitle || data?.data?.title || "Civil Quiz Bank"
          }!`}
      </h2>
      <div className="flex justify-center items-center">
        {data?.status?.purchased ? (
          (() => {
            const purchaseInfo = data?.status;
            const purchasedAt = purchaseInfo?.purchasedAt
              ? new Date(purchaseInfo.purchasedAt)
              : null;
            const duration = purchaseInfo?.subscription_duration
              ? parseInt(purchaseInfo.subscription_duration, 10)
              : 0;

            if (!purchasedAt || isNaN(duration) || duration <= 0) {
              return (
                <div className="text-red-600 font-semibold">
                  ⚠️ Subscription data is missing or invalid.
                </div>
              );
            }

            const expirationDate = new Date(purchasedAt);
            expirationDate.setDate(expirationDate.getDate() + duration);

            const today = new Date();
            const remainingDays = Math.max(
              0,
              Math.ceil((expirationDate - today) / (1000 * 60 * 60 * 24))
            );

            return (
              <p className="font-semibold text-center mt-4">
                You have already purchased quiz bank!
                <span className="text-gray-700">
                  {remainingDays > 0
                    ? ` Your subscription expires in ${remainingDays} day${remainingDays > 1 ? "s" : ""
                    }.`
                    : "Your subscription has expired."}
                </span>
                <br />
                <Link
                  to={studentUrls.studentQuizBankCivil}
                  className="text-blue-600 underline hover:text-blue-800 font-bold"
                >
                  Go to Dashboard
                </Link>{" "}
                to access your quiz bank.
              </p>
            );
          })()
        ) : (
          // userData ? (
          <div className="p-6 my-4 border rounded-xl shadow-xl bg-primary text-center max-w-md mx-auto text-white">
            <h2 className="text-lg font-bold mb-4">Subscription Plan</h2>

            <div className="flex justify-between items-center bg-white/20 px-4 py-2 rounded-lg mb-2">
              <span className="font-medium flex-1 text-left">Duration: </span>
              <span className="font-semibold">
                {content?.subscription_duration} Days
              </span>
            </div>

            <div className="flex justify-between items-center px-4 py-2 rounded-lg mb-4">
              <span className="font-medium flex-1 text-left">Price:</span>
              <span className="font-semibold">
                {discountedPrice ? (
                  <>
                    <span className="line-through text-white/70 mr-2">
                      ${content?.subscription_price}
                    </span>
                    <span className="text-green-300">${discountedPrice}</span>
                  </>
                ) : (
                  <span>${content?.subscription_price}</span>
                )}
              </span>
            </div>
            {discountedPrice && (
              <p className="text-xs text-white/80 mb-2">
                A promo code has been applied. You saved{" "}
                <span className="font-semibold text-green-300">
                  ${parseFloat((content?.subscription_price - discountedPrice).toFixed(2))}
                </span>
                !
              </p>
            )}

            <Button
              type="secondary"
              className="mt-2 w-full"
              onClick={() =>
                !userData ? navigate("/login") : onBuyClick(data?.data)
              }
              disabled={isMutating}
            >
              Buy Now
            </Button>
          </div>
        )}
      </div>
      <Modal
        isOpen={isModalOpen}
        title={"Payment"}
        onCancel={() => {
          setIsModalOpen(false);
          setPaymentConfirmScreen(false);
          setPromoCode("");
          setDiscountedPrice(null);
          setAppliedPromo("");
        }}
        isLoading={isMutating}
        formWidth="sm:max-w-[50%]"
        footer={false}
      >
        {paymentConfirmScreen && (
          <div className="space-y-4">
            {/* Price Display */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:w-1/2">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 font-medium">
                    Total Price
                  </span>
                </div>
                <div className="text-lg font-bold text-gray-900">
                  {discountedPrice ? (
                    <>
                      <span className="line-through text-gray-400 mr-2">
                        ${content?.subscription_price}
                      </span>
                      <span className="text-green-600">${discountedPrice}</span>
                    </>
                  ) : (
                    <span>${content?.subscription_price}</span>
                  )}
                </div>
              </div>
              {discountedPrice && (
                <p className="text-xs text-gray-500">
                  A promo code has been applied. You saved{" "}
                  <span className="font-semibold text-green-600">
                    ${parseFloat((content?.subscription_price - discountedPrice).toFixed(2))}
                  </span>
                  !
                </p>
              )}
            </div>

            {/* Promo Code Input with Apply Button */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Promo Code
              </label>
              <div className="flex flex-wrap gap-2">
                <Input
                  type="text"
                  value={promoCode}
                  onChange={(_, value) => setPromoCode(value)}
                  placeholder="Enter promo code"
                  className="flex-1 p-2"
                />
                <Button
                  type="button"
                  size="small"
                  className="bg-primary text-white text-center my-2"
                  onClick={(e) => {
                    handleApplyPromo(e);
                  }}
                >
                  Apply
                </Button>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              By purchasing, you agree to our{" "}
              <a
                href={publicUrls.TermsOfServices}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-primary font-medium"
              >
                terms & conditions
              </a>
              .
            </p>
            <Button
              type="button"
              className="bg-primary text-white text-center"
              onClick={() => {
                onBuy(data?.data);
              }}
            >
              Purchase
            </Button>
          </div>
        )}

        {clientSecret && (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PaymentForm
              onClose={() => {
                setIsModalOpen(false);
                setClientSecret("");
              }}
              clientSecret={clientSecret}
              mutate={mutate}
              item={data?.data}
            />
          </Elements>
        )}
      </Modal>
    </motion.div>
  );
};

export default QuizBank;
